/**
 * Etapa 5 - Integracoes: provedores de checkout (URL de webhook + segredo),
 * instancia da Evolution e assistente da Vapi.
 *
 * Decisoes nao obvias:
 *  - Os oito provedores do motor: Kiwify, Hotmart, Shopify, o GENERICO
 *    (contrato canonico do Converte Facil, para checkout proprio e qualquer
 *    plataforma ainda sem adaptador -- docs/INTEGRACOES-WEBHOOK-GENERICO.md)
 *    e a onda 1 em beta (Hubla, Eduzz, Cakto, Greenn). O generico
 *    mostra o resumo do contrato na propria etapa: e o que o time tecnico da
 *    empresa pergunta primeiro.
 *  - A URL de webhook ja nasce com o UUID DERIVADO do id previsto da empresa
 *    (identidade.ts): o que a pessoa copia aqui e o que a tela de sucesso e o
 *    detalhe vao mostrar.
 *  - O segredo e `type="password"` com botao de revelar; na previa ele fica so
 *    no estado do formulario e nao aparece na revisao nem no sucesso.
 */

import { useState } from 'react';
import { Badge, Checkbox, Field, Fieldset, Icon, IconButton, Input, Panel } from '../../../../components/ui';
import { idPrevisto, ordenarProvedores, provedoresDasOfertas, sugerirAssistente, sugerirInstancia } from '../../assistente';
import { urlDoWebhook } from '../../identidade';
import type { ProvedorIntegracao } from '../../types';
import { PROVEDORES, PROVEDOR_INFO } from '../../vocabulario';
import { CampoCopiavel } from '../CampoCopiavel';
import { erroDe, type EtapaProps } from './props';

const CONTRATO_GENERICO: readonly string[] = [
  'POST com JSON: event, order_id, amount_cents, customer.phone (e opcionais: nome, e-mail, produto, checkout_url).',
  'Eventos: cart_abandoned, payment_approved, payment_refunded, payment_chargeback, subscription_expired (aceita sinônimos em português).',
  'Assinatura: HMAC-SHA256 em hex dos bytes exatos do corpo, no cabeçalho X-CF-Signature.',
];

export function EtapaIntegracoes(props: EtapaProps): JSX.Element {
  const { form, onChange } = props;
  const [visiveis, setVisiveis] = useState<readonly ProvedorIntegracao[]>([]);

  const id = idPrevisto(form);
  const usadosPelasOfertas = provedoresDasOfertas(form);
  const faltando = usadosPelasOfertas.filter((p) => !form.provedores.includes(p));

  const alternarProvedor = (provider: ProvedorIntegracao, marcado: boolean): void => {
    const lista = marcado ? [...form.provedores, provider] : form.provedores.filter((p) => p !== provider);
    onChange({ provedores: ordenarProvedores(lista) });
  };

  const definirSegredo = (provider: ProvedorIntegracao, valor: string): void => {
    onChange({ segredos: { ...form.segredos, [provider]: valor } });
  };

  const alternarVisivel = (provider: ProvedorIntegracao): void => {
    setVisiveis((lista) => (lista.includes(provider) ? lista.filter((p) => p !== provider) : [...lista, provider]));
  };

  const erroProvedores = erroDe(props, 'provedores');

  return (
    <div className="flex flex-col gap-4">
      <Fieldset legend="Provedores de checkout" description="Marque de onde os eventos de carrinho, pagamento e estorno vão chegar. Para cada um, cole a URL no provedor e informe o segredo.">
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {PROVEDORES.map((provider) => {
            const usadas = form.ofertas.filter((o) => o.provider === provider).length;
            const info = PROVEDOR_INFO[provider];
            return (
              <Checkbox
                key={provider}
                checked={form.provedores.includes(provider)}
                onChange={(marcado) => alternarProvedor(provider, marcado)}
                label={info.label}
                description={`${info.descricao} ${usadas === 0 ? 'Nenhuma oferta usa este provedor.' : `${usadas} ${usadas === 1 ? 'oferta usa' : 'ofertas usam'} este provedor.`}`}
              />
            );
          })}
        </div>
        {erroProvedores !== null ? (
          <p role="alert" className="text-xs text-status-critical">
            {erroProvedores}
          </p>
        ) : null}
        {faltando.length > 0 ? (
          <p className="flex items-start gap-2 text-xs text-ink-muted">
            <Icon name="alerta" size="xs" className="mt-0.5 shrink-0 text-status-warning" />
            <span>
              Há ofertas em {faltando.map((p) => PROVEDOR_INFO[p].curto).join(' e ')} sem o provedor marcado. Sem o webhook, os abandonos dessas ofertas nunca entram na régua.
            </span>
          </p>
        ) : null}
        <p className="flex items-start gap-2 text-xs text-ink-subtle">
          <Icon name="info" size="xs" className="mt-0.5 shrink-0" />
          <span>
            Outra plataforma brasileira (Eduzz, Monetizze, Ticto, Yampi...) conecta hoje pelo Genérico por meio de uma ponte (n8n, Make ou Zapier) que monte o contrato canônico, até
            ganhar adaptador dedicado. Colar a URL do Genérico direto nessas plataformas não funciona: o corpo delas é outro.
          </span>
        </p>
      </Fieldset>

      {form.provedores.length > 0 ? (
        <ul className="flex flex-col gap-4">
          {form.provedores.map((provider) => {
            const segredo = form.segredos[provider];
            const visivel = visiveis.includes(provider);
            const info = PROVEDOR_INFO[provider];
            return (
              <li key={provider}>
                <Panel className="flex flex-col gap-4 p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-base font-semibold text-ink">{info.label}</h3>
                    {segredo.trim() === '' ? (
                      <Badge tone="warning" icon="chave">
                        Sem segredo: o motor responderá 401
                      </Badge>
                    ) : (
                      <Badge tone="good" icon="chave">
                        Segredo informado
                      </Badge>
                    )}
                  </div>
                  <CampoCopiavel label="URL do webhook" value={urlDoWebhook(provider, id)} hint={info.hint} />
                  {provider === 'generic' ? (
                    <div className="rounded-lg border border-line bg-elevated px-3 py-2.5">
                      <p className="text-[10px] font-semibold uppercase tracking-wide text-ink-subtle">Contrato canônico, em três linhas</p>
                      <ul className="mt-1.5 flex flex-col gap-1 text-xs text-ink-muted">
                        {CONTRATO_GENERICO.map((linha) => (
                          <li key={linha} className="flex items-start gap-1.5">
                            <span aria-hidden="true" className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-ink-subtle" />
                            {linha}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                  <Field label="Segredo de assinatura" hint={info.segredoHint}>
                    {({ id: campoId, describedBy }) => (
                      <div className="flex items-center gap-2">
                        <Input id={campoId} aria-describedby={describedBy} type={visivel ? 'text' : 'password'} value={segredo} onChange={(e) => definirSegredo(provider, e.target.value)} autoComplete="off" spellCheck={false} className="font-mono text-xs" />
                        <IconButton icon="olho" label={visivel ? 'Ocultar segredo' : 'Mostrar segredo'} variant="secondary" aria-pressed={visivel} onClick={() => alternarVisivel(provider)} />
                      </div>
                    )}
                  </Field>
                </Panel>
              </li>
            );
          })}
        </ul>
      ) : null}

      <Fieldset legend="Canais de recuperação" description="Quem liga e quem manda WhatsApp em nome da empresa. O número conectado costuma ser o do comercial: quem responde a um toque cai com a vendedora.">
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Instância da Evolution API (WhatsApp)" required hint={`Sugestão: ${sugerirInstancia(form.nome) === '' ? 'cf-<slug-da-empresa>' : sugerirInstancia(form.nome)}`} error={erroDe(props, 'evolutionInstancia')}>
            {({ id: campoId, describedBy, invalid }) => (
              <Input id={campoId} aria-describedby={describedBy} aria-invalid={invalid} value={form.evolutionInstancia} onChange={(e) => onChange({ evolutionInstancia: e.target.value })} autoComplete="off" spellCheck={false} className="font-mono text-xs" />
            )}
          </Field>
          <Field label="Assistente de voz (Vapi)" required hint={`Sugestão: ${sugerirAssistente(form.nome) === '' ? 'Assistente <nome da empresa>' : sugerirAssistente(form.nome)}`} error={erroDe(props, 'vapiAssistente')}>
            {({ id: campoId, describedBy, invalid }) => <Input id={campoId} aria-describedby={describedBy} aria-invalid={invalid} value={form.vapiAssistente} onChange={(e) => onChange({ vapiAssistente: e.target.value })} autoComplete="off" />}
          </Field>
        </div>
      </Fieldset>
    </div>
  );
}
